/**
 * Sales EOM — page breaking.
 *
 * Long tables run onto "(CONTINUED)" pages, each its own A4 sheet with its own page number,
 * instead of growing one tall section that the PDF exporter would then slice wherever a
 * sheet happened to end — through a row, under a repeated footer, with every slice
 * claiming the same page number.
 *
 * Heights here are ESTIMATES, in CSS pixels at the 960px page width. They only decide
 * where a table first breaks. The report then measures every page in the browser and
 * pushes rows forward off any page that overflows (`budget` takes those corrections), so
 * an estimate that runs short costs one extra layout pass, never a sliced page. Under jsdom,
 * which has no layout, the estimates alone decide — which is what the tests see.
 *
 * Pure functions. No React, no DOM.
 */

/** A4 height over width. A page is exactly one sheet at whatever width it renders. */
export const A4_RATIO = 297 / 210;

/** Content height a page holds below its top padding and above its footer, at 960px. */
export const PAGE_BUDGET = 1250;

/** Clearance kept free above the footer when a page is measured. */
export const FOOTER_RESERVE = 64;

// Measured in Chrome on the September 2026 report. A lede's bottom margin and a rule's top
// margin collapse into the margin of whatever follows, so they are counted once, there.
export const H = {
  header: 80, // eyebrow + page title
  ledeLine: 25.5,
  ledePad: 14,
  ledeChars: 96,
  blockGap: 28, // space above a quadrant block
  blockTitle: 35, // quadrant title with its axis label, underlined
  ruleLine: 20.5,
  rulePad: 14,
  ruleChars: 118,
  quadTableHead: 54, // table margin + header row
  quadRow: 37,
  empty: 40,
  ledgerTableHead: 68,
  catRow: 43, // a category heading row
  itemRow: 39, // a dish row under it
  totalsRow: 43,
  readPad: 102, // margin + padding + heading of a read box
  readLine: 25.6,
  readChars: 92,
  footnotePad: 37,
  footnoteLine: 18,
  footnoteChars: 110,
};

const lines = (text, perLine) => (text ? Math.max(1, Math.ceil(String(text).length / perLine)) : 0);

export const ledeHeight = (text) => (text ? H.ledePad + lines(text, H.ledeChars) * H.ledeLine : 0);
export const readHeight = (text) => (text ? H.readPad + lines(text, H.readChars) * H.readLine : 0);
export const footnoteHeight = (text) =>
  text ? H.footnotePad + lines(text, H.footnoteChars) * H.footnoteLine : 0;
export const ruleHeight = (text) => H.rulePad + lines(text, H.ruleChars) * H.ruleLine;

/**
 * Lay a run of headed tables (the quadrant blocks of section 03) onto pages.
 *
 * blocks: [{ key, rows, head, contHead, rowHeight, emptyHeight }]
 *   head      — height above the first rows: gap, title, rule, table header
 *   contHead  — height above continued rows: gap, title, table header (no rule)
 * Returns one array per page of { key, from, to, continued } segments.
 */
export function paginateBlocks(blocks, { firstHeader, contHeader, budget }) {
  const pages = [];
  let page = null;
  const open = () => {
    page = { segments: [], used: pages.length === 0 ? firstHeader : contHeader };
    pages.push(page);
  };
  const room = () => budget(pages.length - 1) - page.used;
  open();

  for (const block of blocks) {
    const total = block.rows.length;

    if (total === 0) {
      const need = block.head + block.emptyHeight;
      if (need > room() && page.segments.length) open();
      page.segments.push({ key: block.key, from: 0, to: 0, continued: false });
      page.used += need;
      continue;
    }

    let i = 0;
    while (i < total) {
      const continued = i > 0;
      const head = continued ? block.contHead : block.head;
      const left = total - i;
      let fit = Math.floor((room() - head) / block.rowHeight);
      // A heading with fewer than two rows under it at the foot of a page reads as a stray;
      // it starts the next page instead, unless this page is still empty.
      if (fit < Math.min(2, left) && page.segments.length) {
        open();
        continue;
      }
      fit = Math.max(1, Math.min(fit, left));
      page.segments.push({ key: block.key, from: i, to: i + fit, continued });
      page.used += head + fit * block.rowHeight;
      i += fit;
      if (i < total) open();
    }
  }

  return pages.map((p) => p.segments);
}

/**
 * Lay one long table (the category ledger of section 05) onto pages, ending with a tail
 * (totals row, read box, footnote) that must sit under the last rows.
 *
 * keepWithNext(row) marks a row that must not be the last on a page — a category heading
 * belongs with its first dish.
 * Returns [{ from, to, last }]. A page with from === to carries the tail alone.
 */
export function paginateRows(
  rows,
  { firstHeader, contHeader, head, rowHeight, tail = 0, budget, keepWithNext = () => false }
) {
  if (!rows.length) return [{ from: 0, to: 0, last: true }];

  const pages = [];
  let i = 0;
  while (i < rows.length) {
    const index = pages.length;
    const cap = budget(index) - (index === 0 ? firstHeader : contHeader) - head;

    let used = 0;
    let j = i;
    while (j < rows.length && used + rowHeight(rows[j]) <= cap) {
      used += rowHeight(rows[j]);
      j += 1;
    }
    if (j === i) {
      // Not even one row fits: place it anyway, or the loop never ends.
      used = rowHeight(rows[i]);
      j = i + 1;
    }

    if (j >= rows.length) {
      if (used + tail <= cap) {
        pages.push({ from: i, to: j, last: true });
        break;
      }
      // The tail does not fit under the last rows: carry rows forward so it has company.
      while (j > i + 1 && used + tail > cap) {
        j -= 1;
        used -= rowHeight(rows[j]);
      }
      if (j >= rows.length) {
        // One row left and still no room: the tail takes a page of its own.
        pages.push({ from: i, to: j, last: false });
        pages.push({ from: j, to: j, last: true });
        break;
      }
    }

    while (j - i > 1 && keepWithNext(rows[j - 1])) j -= 1;
    pages.push({ from: i, to: j, last: false });
    i = j;
  }
  return pages;
}
